import { useSyncExternalStore } from "react";

/**
 * Densidade da grade e janela da timeline.
 *
 * Os dias da timeline saem da largura *útil* do calendário (main, já sem
 * sidebar), não da janela. 28 colunas numa tela de 1920px com sidebar de 288px
 * esmagam os chips e cortam os ícones. Cada coluna tem um mínimo em pixels;
 * o que não cabe vira menos dias, e o restante se navega pelas setas.
 */

export const TIMELINE_MIN_DAYS = 7;
export const TIMELINE_MAX_DAYS = 28;

/** Sidebar visível a partir do `md` do shell. */
const SIDEBAR_BREAKPOINT = 768;
const SIDEBAR_WIDTH_PX = 288;

export interface TimelineChrome {
  labelPx: number;
  dayPx: number;
}

/**
 * Tokens da faixa (label + coluna) alinhados aos `@container calendar` do CSS.
 * Valores em px assumem root 16px.
 */
export function timelineChromeForContentWidth(contentWidth: number): TimelineChrome {
  if (contentWidth < 400) return { labelPx: 116, dayPx: 66 };
  if (contentWidth < 768) return { labelPx: 132, dayPx: 72 };
  if (contentWidth < 1024) return { labelPx: 156, dayPx: 76 };
  if (contentWidth < 1280) return { labelPx: 184, dayPx: 80 };
  return { labelPx: 248, dayPx: 88 };
}

export function timelineDaysForContentWidth(contentWidth: number): number {
  const { labelPx, dayPx } = timelineChromeForContentWidth(contentWidth);
  const fitted = Math.floor((Math.max(0, contentWidth) - labelPx) / dayPx);
  return Math.min(TIMELINE_MAX_DAYS, Math.max(TIMELINE_MIN_DAYS, fitted));
}

/**
 * Atalho para testes e SSR: estima a área útil a partir da janela.
 * Em runtime o hook mede o `[data-app-main]`.
 */
export function timelineDaysForWidth(windowWidth: number): number {
  const chrome = windowWidth >= SIDEBAR_BREAKPOINT ? SIDEBAR_WIDTH_PX : 0;
  return timelineDaysForContentWidth(Math.max(0, windowWidth - chrome));
}

function measureContentWidth(): number {
  if (typeof document === "undefined") return 1280;
  const main = document.querySelector("[data-app-main]");
  if (main instanceof HTMLElement && main.clientWidth > 0) return main.clientWidth;
  return window.innerWidth;
}

let cachedWidth = -1;
let cachedDays = 14;

function getTimelineDays(): number {
  const width = measureContentWidth();
  if (width === cachedWidth) return cachedDays;
  cachedWidth = width;
  cachedDays = timelineDaysForContentWidth(width);
  return cachedDays;
}

function subscribeShellWidth(onChange: () => void): () => void {
  let observer: ResizeObserver | null = null;
  const attach = (): void => {
    const main = document.querySelector("[data-app-main]");
    if (!(main instanceof HTMLElement) || observer !== null) return;
    observer = new ResizeObserver(onChange);
    observer.observe(main);
  };

  attach();
  const timer = window.setTimeout(attach, 0);
  window.addEventListener("resize", onChange);
  return () => {
    window.clearTimeout(timer);
    window.removeEventListener("resize", onChange);
    observer?.disconnect();
  };
}

function subscribeQuery(query: string, onChange: () => void): () => void {
  const media = window.matchMedia(query);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

export function useMediaQuery(query: string, serverValue = false): boolean {
  return useSyncExternalStore(
    (onChange) => subscribeQuery(query, onChange),
    () => window.matchMedia(query).matches,
    () => serverValue,
  );
}

export function useTimelineDays(): number {
  return useSyncExternalStore(subscribeShellWidth, getTimelineDays, () => 14);
}

export function useNarrowCalendar(): boolean {
  return useMediaQuery("(max-width: 639px)");
}

export function useCalendarDensity(): "phone" | "tablet" | "desktop" {
  const phone = useMediaQuery("(max-width: 639px)");
  const tablet = useMediaQuery("(max-width: 1023px)");
  if (phone) return "phone";
  if (tablet) return "tablet";
  return "desktop";
}
